import { ToastEventBus, ToastMessageOptions } from 'primevue'
import { useLocalStorage } from '@vueuse/core'
import { i18n } from '@/modules/i18n'

const defaultToastOptions: ToastMessageOptions = {
  life: 5000,
  closable: true,
}

const getToastRef = () => {
  const toastRef = useLocalStorage<Date[]>('toastCount', [])

  const now = new Date()

  toastRef.value = toastRef.value.filter((date) => {
    return new Date(date).getTime() > now.getTime() - 60 * 1000
  })

  return toastRef
}

const incrementToastCount = () => {
  const toastRef = getToastRef()

  toastRef.value.push(new Date())
}

const DUPLICATE_WINDOW_MS = 5000
let lastToastKey: string | undefined
let lastToastAt = 0

const add = (options: ToastMessageOptions) => {
  const key = `${options.severity}:${options.summary}:${options.detail}`
  const now = Date.now()

  if (key === lastToastKey && now - lastToastAt < DUPLICATE_WINDOW_MS) {
    return
  }

  lastToastKey = key
  lastToastAt = now

  if (getToastRef().value.length > 5) {
    return
  }

  ToastEventBus.emit('add', options)

  incrementToastCount()
}

type ToastSeverity = 'error' | 'success' | 'warn' | 'info'

type ToastFunction = (detail: string, options?: ToastMessageOptions) => void

/**
 * Creates a toast function with the specified severity and translation key
 */
const createToastFunction = (
  severity: ToastSeverity,
  translationKey: string,
): ToastFunction => {
  return (detail: string, options?: ToastMessageOptions) => add({
    summary: i18n.global.t(translationKey),
    detail,
    severity,
    ...defaultToastOptions,
    ...options,
  })
}

export const useToast = (): Record<ToastSeverity, ToastFunction> => {
  return {
    error: createToastFunction('error', 'toast.error.summary'),
    success: createToastFunction('success', 'toast.success.summary'),
    warn: createToastFunction('warn', 'toast.warn.summary'),
    info: createToastFunction('info', 'toast.info.summary'),
  }
}
